import { PropsWithChildren } from 'react';
import {
  ConfirmationModal,
  ConfirmationModalProps,
  NotificationMessage
} from '@app/page-components';
import {
  NotificationType,
  Public,
  Subject
} from '@app/types';
import { createUuid } from '@app/utilities';

export type IGlobalModalsService = Public<GlobalModalsService>;

/** Stores the state of global modals that are rendered by the @see GlobalModals component. */
export class GlobalModalsService {
  activeDialogSubject = new Subject<JSX.Element | null>();
  private _dialogs: (JSX.Element | null)[] = [];
  private _openModalIds = new Set<string>();

  get activeDialog(): JSX.Element | null {
    if (!this._dialogs.length) {
      return null;
    }
    return this._dialogs[this._dialogs.length - 1];
  }
  set activeDialog(newActiveDialog: JSX.Element | null) {
    if (newActiveDialog) {
      this._dialogs.push(newActiveDialog);
    } else {
      this._dialogs.pop();
    }
    this.activeDialogSubject.next(this.activeDialog);
  }

  openConfirmationModal(
    props: Omit<ConfirmationModalProps, 'isOpen' | 'onComplete'> & { id?: string } = {}
  ): Promise<boolean> {
    const { id = createUuid(), ...modalProps } = props;
    if (this._openModalIds.has(id)) {
      throw new Error(`Modal ID already in use. ${window.l.notification.ask_for_help}`);
    }
    this._openModalIds.add(id);
    return new Promise((resolve) => {
      this.activeDialog = (
        <ConfirmationModal
          {...modalProps}
          onComplete={(proceed) => {
            if (id != null) {
              this._openModalIds.delete(id);
            }
            this.activeDialog = null;
            resolve(proceed);
          }}
        />
      );
    });
  }

  /** Prompts the user to confirm discarding unsaved changes. Resolves `true` if they choose to discard. */
  async confirmDiscardChanges(message?: string): Promise<boolean> {
    return this.openConfirmationModal({
      children: message ?? 'You have unsaved changes that will be lost.',
      labels: {
        cancelButton: 'Keep Working',
        continueButton: 'Discard',
        header: 'Discard changes?'
      }
    });
  }

  async openNotificationModal(
    {
      children = null,
      header,
      id,
      type
    }: PropsWithChildren<{
      header: string;
      type: NotificationType;
      id?: string;
    }>
  ): Promise<void> {
    await this.openConfirmationModal({
      children,
      hideContinue: true,
      id,
      labels: {
        cancelButton: 'Close',
        header: (
          <NotificationMessage
            children={header}
            type={type}
          />
        )
      }
    });
  }
}
